import React, { useEffect, useState } from "react";

import { Transition } from "@headlessui/react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

import useVisualViewport from "../utils/hooks/useVisualViewport";
import { useGetIntegrationQuery } from "../utils/queries/integration";
import { getChatButtonSessionStorage } from "../utils/session-storage";
import { useWebmessenger } from "../utils/webmessenger";

function Button({ onClick }) {
    const { operator, integration, unreadMessagesCount } = useWebmessenger();

    const { windowSize } = useVisualViewport();

    const chatButtonStorage = getChatButtonSessionStorage();

    const [customText, setCustomText] = useState(null);
    const [show, setShow] = useState(true);
    const [showChatButtonAnimation, setShowChatButtonAnimation] =
        useState(false);

    useGetIntegrationQuery(integration, {
        onSuccess: ({ data }) => {
            setCustomText(data.chatButtonText);
        },
        enabled: !!integration,
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const isMobile = windowSize.width <= 768;
    const hasAnimation = !!chatButtonStorage;

    useEffect(() => {
        if (chatButtonStorage) {
            const timeout = setTimeout(() => {
                setShowChatButtonAnimation(true);
            }, 6000);

            return () => clearTimeout(timeout);
        }

        return undefined;
    }, [chatButtonStorage]);

    return (
        <Transition
            as="div"
            show={show}
            appear
            className={classNames("ek-chat__button", {
                [`ek-chat__button--animation--${
                    isMobile ? "mobile" : "desktop"
                } ek-chat__button--entered`]:
                    hasAnimation && showChatButtonAnimation,
            })}
            enter="ek-chat__button--enter"
            entered="ek-chat__button--entered"
            leave="ek-chat__button--leave"
            afterLeave={() => {
                onClick();
            }}
            onClick={() => setShow(false)}
        >
            {operator && (
                <>
                    {operator.face && (
                        <div className="ek-chat__avatar">
                            <img
                                src={
                                    operator.type === "agent"
                                        ? `${process.env.REACT_APP_FILE}/files/agents-logo/${operator.face}`
                                        : `${process.env.REACT_APP_FILE}/files/avatars/${operator.face}`
                                }
                                alt=""
                                className="ek-chat__avatar--img"
                            />
                        </div>
                    )}
                    <p>
                        <FormattedMessage
                            id="WEBMESSENGER.BUTTON.DISCUSS_WITH"
                            defaultMessage="Discutez avec {operator}"
                            values={{ operator: operator.name }}
                        />
                    </p>
                </>
            )}

            {!operator &&
                (customText ? (
                    <p>{customText}</p>
                ) : (
                    <p>
                        <FormattedMessage
                            id="WEBMESSENGER.BUTTON.AVAILABLE"
                            defaultMessage="Discutons ensemble"
                        />
                    </p>
                ))}
            <div className="ek-chat__icon">
                <svg viewBox="0 0 24 24" version="1.1">
                    <g
                        stroke="none"
                        strokeWidth="1"
                        fill="none"
                        fillRule="evenodd"
                    >
                        <path
                            d="M12.1 2C6.472 2 1.719 6.433 1.719 11.906a9.28 9.28 0 0 0 1.663 5.258l-1.663 2.988s-.418.828-.096 1.311a1.14 1.14 0 0 0 1.33.477c.526-.146 4.484-1.282 4.484-1.282a10.861 10.861 0 0 0 4.867 1.142c5.632 0 10.197-4.436 10.197-9.906C22.5 6.424 17.733 2 12.1 2zM8.06 13.477a1.208 1.208 0 0 1-1.106-.76 1.218 1.218 0 0 1 .891-1.646 1.208 1.208 0 0 1 1.238.517c.133.199.204.433.204.672a1.23 1.23 0 0 1-1.227 1.217zm4.01-.609a1.208 1.208 0 0 1-1.104-.761 1.217 1.217 0 0 1 .89-1.644 1.207 1.207 0 0 1 1.238.515c.133.198.204.432.206.67a1.23 1.23 0 0 1-1.23 1.22zm4.012.609a1.208 1.208 0 0 1-1.106-.76 1.217 1.217 0 0 1 .891-1.646 1.208 1.208 0 0 1 1.238.517c.132.199.203.433.204.672a1.23 1.23 0 0 1-1.227 1.217z"
                            fill="currentColor"
                        />
                    </g>
                </svg>
                {unreadMessagesCount && unreadMessagesCount > 0 ? (
                    <span className="ek-chat__icon--unreadMessages">
                        {unreadMessagesCount > 9 ? (
                            <>
                                9
                                <span className="ek-chat__icon--unreadMessages--exceeds">
                                    +
                                </span>
                            </>
                        ) : (
                            unreadMessagesCount
                        )}
                    </span>
                ) : (
                    <span className="ek-chat__icon--status" />
                )}
            </div>
            <span
                className="ek-chat__button--typing"
                style={{ display: "none" }}
            >
                <span />
                <span />
                <span />
            </span>
            <span className="ek-chat__button--new" style={{ display: "none" }}>
                1
            </span>
        </Transition>
    );
}

Button.propTypes = {
    onClick: PropTypes.func,
};

Button.defaultProps = {
    onClick: () => {},
};

export default Button;
